/**
 * Shared registration wizard components.
 *
 * Extracted from step-vehicle-product.tsx so they can be reused by
 * step-property-product.tsx and step-product-only.tsx.
 */

import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  Building2,
  X,
  Package,
  Settings2,
  Plus,
  UserPlus,
  Pencil,
  RefreshCw,
} from "lucide-react";
import { RegistrationPanel } from "./layout";
import {
  useProductCategories,
  usePublishedProducts,
  usePartnershipSearch,
  useProductSearch,
  useProductTemplate,
  useProductTemplates,
} from "@/admin/hooks/use-products";
import { useEntityTypeByKey } from "@/admin/hooks/use-entities";
import { useStockProductResolve } from "@/admin/hooks/use-stock-product-resolve";
import {
  isAvailableStockStatus,
  isTakenStockMatch,
  lockableStockMatch,
  lockedCatalogueLabels,
  stockBackedIdentityLabels,
} from "./stock-resolved-product";
import type {
  PartnershipOption,
  PartnershipSearchOptions,
} from "@/admin/hooks/use-products";
import { useOrg } from "@/admin/hooks/use-org";
import { usePartnerPickerScope } from "@/admin/hooks/use-partner-picker-scope";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { usePartnershipDetail } from "@/admin/hooks/use-partnerships";
import { PartnerCreateDialog } from "@/admin/components/shared/partner-create-inline-form";
import type { DuplicatePartnerCandidate } from "@/admin/hooks/use-manual-partner";
import type { Partnership } from "@/admin/types/organisations";
import type { WizardState } from "@/admin/pages/registrations/new-registration-page";
import type {
  Product,
  ProductTemplate,
  RegistrationFormMetadata,
  WarrantyTemplateSummary,
} from "@/admin/types/products";
import { FilterableDetailList } from "@/admin/components/shared/filterable-detail-list";
import { Input } from "@/shared/components/ui/input";
import { SearchInput } from "@/shared/components/ui/search-input";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { CategoryIcon } from "@/admin/components/category-icons";
import { formatDurationFromMonths } from "@/admin/lib/formatting";
import {
  normaliseRegistrationField,
  buildFormFieldFromMeta,
  useForm,
} from "@/shared/lib/forms";
import type { FormFieldMeta } from "@/shared/lib/forms";
import {
  asStockSearchField,
  readStockConfig,
  unwrapStockFormValue,
  wrapStockFormValue,
} from "@/shared/lib/value-stock-field";
import { useRegisterFormValidator } from "./wizard-validation";
import {
  availabilityKey,
  type AvailabilityResult,
} from "@/admin/hooks/use-availability-rules";
import type { WarrantyAvailabilityMap } from "@/admin/hooks/use-warranty-availability";

/**
 * Look up the availability decision (if any) for a (template, option) pair.
 * When `availability` is undefined or has no entry, the option is treated
 * as available - the server enforces the rule on submit anyway.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function getOptionAvailability(
  availability: WarrantyAvailabilityMap | undefined,
  templateUuid: string,
  optionUuid: string | null,
): AvailabilityResult | undefined {
  return availability?.byOption.get(availabilityKey(templateUuid, optionUuid));
}

/**
 * Returns true when a warranty template has at least one option the user
 * can actually select. Templates whose options are all blocked by
 * availability rules can never produce a complete selection, so they
 * should be hidden from the chooser to avoid the "selectable but
 * Continue stays disabled" trap.
 *
 * Templates with zero options are treated as selectable on their own -
 * the wizard's `isWarrantySelectionResolved` already accepts that shape.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function hasSelectableWarrantyOption(
  template: WarrantyTemplateSummary,
  availability?: WarrantyAvailabilityMap,
): boolean {
  if (template.options.length === 0) return true;
  return template.options.some((opt) => {
    const decision = getOptionAvailability(
      availability,
      template.uuid,
      opt.uuid,
    );
    return decision ? decision.available : true;
  });
}

// eslint-disable-next-line react-refresh/only-export-components
export function getDefaultWarrantyOptionUuid(
  warrantyTemplate: WarrantyTemplateSummary | null | undefined,
  availability?: WarrantyAvailabilityMap,
): string | null {
  if (!warrantyTemplate) return null;
  // Honour the rule-pinned option first - but only when it actually
  // matches one of the template's options. If the backend ever ships a
  // ``pre_assigned_option_uuid`` that doesn't appear in ``options``
  // (stale rule, race between rule edit and template edit, payload
  // bug), returning it would leave ``isWarrantySelectionResolved``
  // permanently false and Continue would silently reject every click.
  // Falling through to the first available option keeps the wizard
  // navigable while still surfacing the policy_code chip resolved by
  // the rule.
  if (warrantyTemplate.pre_assigned_option_uuid) {
    const pinned = warrantyTemplate.pre_assigned_option_uuid;
    if (warrantyTemplate.options.some((o) => o.uuid === pinned)) {
      return pinned;
    }
  }
  const isAvailable = (uuid: string) => {
    const decision = getOptionAvailability(
      availability,
      warrantyTemplate.uuid,
      uuid,
    );
    return decision ? decision.available : true;
  };
  const candidates = warrantyTemplate.options.filter((opt) =>
    isAvailable(opt.uuid),
  );
  // Default to the first available option so selecting a warranty class
  // immediately produces a complete selection. Users can still pick a
  // different option from the chooser; strict tenants are unaffected
  // because the resolved ``pre_assigned_option_uuid`` is honoured above.
  return candidates[0]?.uuid ?? null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function isWarrantySelectionResolved({
  warrantyTemplates,
  selectedClassUuid,
  selectedOptionUuid,
}: {
  warrantyTemplates: WarrantyTemplateSummary[];
  selectedClassUuid: string | null;
  selectedOptionUuid: string | null;
}): boolean {
  if (warrantyTemplates.length === 0) return true;
  if (!selectedClassUuid) return false;

  const activeClass = warrantyTemplates.find(
    (template) => template.uuid === selectedClassUuid,
  );
  if (!activeClass) return false;

  if (activeClass.options.length === 0) return true;

  return (
    !!selectedOptionUuid &&
    activeClass.options.some((option) => option.uuid === selectedOptionUuid)
  );
}

/**
 * True when the user has an actual duration choice to make for this
 * template - i.e. there is more than one option AND no
 * :class:`AvailabilityRule` has pinned a specific option via
 * ``pre_assigned_option_uuid``. Surfaces use this to suppress duration
 * UI when the choice is degenerate so the wizard doesn't waste real
 * estate naming a value the user can't change. A pre-assigned option
 * is treated the same as "single option" for display purposes - from
 * the user's perspective the rule already decided. Tenants whose
 * templates legitimately offer multiple user-selectable durations (e.g.
 * Supagard with no narrowing rule) keep the picker.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function shouldShowWarrantyDuration(
  template: WarrantyTemplateSummary | null | undefined,
): boolean {
  if (!template) return false;
  if (template.pre_assigned_option_uuid) return false;
  return template.options.length > 1;
}

// eslint-disable-next-line react-refresh/only-export-components
export function formatWarrantyTypeLabel(warrantyType: string) {
  return warrantyType
    .replace(/_WARRANTY$/i, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

// eslint-disable-next-line react-refresh/only-export-components
export function getVariantAxisLabelFromTemplate(
  template:
    | Pick<
        ProductTemplate,
        "variant_short_name_format" | "variant_long_name_format" | "attributes"
      >
    | null
    | undefined,
) {
  const format =
    template?.variant_short_name_format ||
    template?.variant_long_name_format ||
    "";
  const codes = Array.from(format.matchAll(/\{([a-z0-9_]+)\}/gi)).map(
    (m) => m[1],
  );
  const attrs = template?.attributes ?? [];
  const seen = new Set<string>();
  const axes = codes
    .filter((code) => {
      if (seen.has(code)) return false;
      seen.add(code);
      return true;
    })
    .map((code) => attrs.find((a) => a.code_name === code))
    .filter(
      (a): a is NonNullable<typeof a> =>
        !!a && a.attribute_level === "PRODUCT_VARIANT",
    );

  if (axes.length === 0) return "variant";
  return axes.map((a) => a.display_name).join(" & ");
}

function pluraliseCountLabel(label: string) {
  const normalised = label.trim().toLowerCase();
  if (!normalised) return "variants";
  if (normalised.includes("&")) return `${normalised} options`;
  if (/[sxz]$|[cs]h$/.test(normalised)) return `${normalised}es`;
  if (/[^aeiou]y$/.test(normalised)) return `${normalised.slice(0, -1)}ies`;
  return `${normalised}s`;
}

function formatVariantCountLabel(count: number, axisLabel?: string) {
  const label = axisLabel?.trim() || "variant";
  return `${count} ${count === 1 ? label.toLowerCase() : pluraliseCountLabel(label)}`;
}

// ---------------------------------------------------------------------------
// Warranty Selector
// ---------------------------------------------------------------------------

export function WarrantySelector({
  warrantyTemplates,
  selectedClassUuid,
  selectedOptionUuid,
  onSelectClass,
  onSelectOption,
  standalone = false,
  availability,
}: {
  warrantyTemplates: WarrantyTemplateSummary[];
  selectedClassUuid: string | null;
  selectedOptionUuid: string | null;
  onSelectClass: (uuid: string) => void;
  onSelectOption: (uuid: string) => void;
  standalone?: boolean;
  availability?: WarrantyAvailabilityMap;
}) {
  const singleClass = warrantyTemplates.length === 1;
  const activeClass = warrantyTemplates.find(
    (wc) => wc.uuid === selectedClassUuid,
  );
  // Strict mode: the rule pre-assigns a duration and the wizard must not
  // let the user choose something different.
  const preAssignedOption = activeClass?.pre_assigned_option_uuid
    ? (activeClass.options.find(
        (o) => o.uuid === activeClass.pre_assigned_option_uuid,
      ) ?? null)
    : null;
  const hasPreAssignedOption = !!preAssignedOption;
  // Only surface duration UI when the user actually has a choice to
  // make. If the template only ships one option (or zero), the duration
  // is decided upstream and showing it here just adds noise.
  const showDuration = shouldShowWarrantyDuration(activeClass);
  const isFullyResolved = isWarrantySelectionResolved({
    warrantyTemplates,
    selectedClassUuid,
    selectedOptionUuid,
  });

  return (
    <div
      className={
        standalone ? "" : "mt-4 border-t-[0.5px] border-t-[var(--rule)] pt-4"
      }
    >
      {!standalone && (
        <p className="mb-3 text-[10px] font-light uppercase tracking-[0.16em] text-[var(--ink-3)]">
          {isFullyResolved ? "Warranty" : "Select Warranty"}
        </p>
      )}
      {/* Class selection - show if multiple classes */}
      {!singleClass && (
        <FilterableDetailList
          items={warrantyTemplates}
          getItemKey={(item) => item.uuid}
          getSearchText={(item) =>
            [
              item.name,
              formatWarrantyTypeLabel(item.warranty_type),
              ...item.options.map((option) =>
                option.is_lifetime
                  ? "Lifetime"
                  : option.display_name ||
                    formatDurationFromMonths(option.duration_in_months),
              ),
            ].join(" ")
          }
          getItemOnClick={(item) => () => onSelectClass(item.uuid)}
          itemLabel="warranties"
          scrollable
          maxHeight="max-h-[360px]"
          searchPlaceholder="Search warranties"
          emptyFilteredState="No warranties match this filter."
          renderItem={(item) => item}
          renderList={({ items, getItemKey, getItemOnClick }) => (
            <div className="flex flex-col gap-1.5">
              {items.map((wc, index) => {
                const isSelected = wc.uuid === selectedClassUuid;
                const onClick = getItemOnClick?.(wc);

                return (
                  <Button
                    key={getItemKey(wc, index)}
                    type="button"
                    variant="outline"
                    onClick={onClick}
                    className={cn(
                      "flex h-auto w-full cursor-pointer items-center justify-between rounded-[4px] px-3 py-2 text-left font-light shadow-none transition-all duration-150",
                      isSelected
                        ? "border-[var(--green-border)] bg-[var(--green-bg)] hover:bg-[var(--green-bg)]"
                        : "border-[var(--rule-strong)] bg-[var(--bg)] hover:border-[var(--ink-3)] hover:bg-[var(--bg)]",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      {isSelected && (
                        <Check
                          size={12}
                          className="shrink-0 text-[var(--green)]"
                        />
                      )}
                      <span
                        className={cn(
                          "text-[13px]",
                          isSelected
                            ? "font-light text-[var(--ink)]"
                            : "text-[var(--ink)]",
                        )}
                      >
                        {wc.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {wc.policy_code && (
                        <span
                          className="whitespace-nowrap rounded-[3px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-1.5 py-0.5 font-mono text-[9px] tracking-[0.04em] text-[var(--ink-2)]"
                          title="Policy number resolved by the matching availability rule"
                        >
                          {wc.policy_code}
                        </span>
                      )}
                      <span className="whitespace-nowrap rounded-full bg-[var(--green-bg)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--green)]">
                        {formatWarrantyTypeLabel(wc.warranty_type)}
                      </span>
                    </div>
                  </Button>
                );
              })}
            </div>
          )}
        />
      )}
      {/* Single class - compact single-line summary, styled as
          "selected" to mirror the multi-class selected card so the
          user can see at a glance that the warranty is committed and
          they're free to continue. */}
      {singleClass && activeClass && (
        <div className="flex items-center gap-2 rounded-[4px] border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-3 py-2">
          <Check size={12} className="shrink-0 text-[var(--green)]" />
          <span className="text-[13px] font-light text-[var(--ink)]">
            {activeClass.name}
          </span>
          <span className="ml-auto flex items-center gap-1.5">
            {activeClass.policy_code && (
              <span
                className="whitespace-nowrap rounded-[3px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-1.5 py-0.5 font-mono text-[9px] tracking-[0.04em] text-[var(--ink-2)]"
                title="Policy number resolved by the matching availability rule"
              >
                {activeClass.policy_code}
              </span>
            )}
            <span className="whitespace-nowrap rounded-full bg-[var(--bg)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--green)]">
              {formatWarrantyTypeLabel(activeClass.warranty_type)}
            </span>
          </span>
        </div>
      )}
      {/* Pre-assigned duration - strict mode locks the option to the rule */}
      {activeClass && hasPreAssignedOption && !singleClass && showDuration && (
        <div className="mt-3">
          <p className="mb-2 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
            Duration
          </p>
          <div className="inline-flex items-center gap-2 rounded-[4px] border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-3 py-1.5 text-[12px] text-[var(--green)]">
            <Check size={12} className="shrink-0" />
            <span>
              {preAssignedOption!.is_lifetime
                ? "Lifetime"
                : preAssignedOption!.display_name ||
                  formatDurationFromMonths(
                    preAssignedOption!.duration_in_months,
                  )}
            </span>
          </div>
        </div>
      )}
      {/* Option selection - show if class is selected, no pre-assigned option, and multiple options */}
      {activeClass && !hasPreAssignedOption && showDuration && (
        <div className="mt-3">
          <p className="mb-2 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
            Duration
          </p>
          <div className="flex flex-wrap gap-1.5">
            {activeClass.options.map((opt) => {
              const isSelected = opt.uuid === selectedOptionUuid;
              const decision = getOptionAvailability(
                availability,
                activeClass.uuid,
                opt.uuid,
              );
              const isDisabled = decision ? !decision.available : false;
              const label = opt.is_lifetime
                ? "Lifetime"
                : opt.display_name ||
                  formatDurationFromMonths(opt.duration_in_months);
              return (
                <Button
                  key={opt.uuid}
                  type="button"
                  variant="outline"
                  disabled={isDisabled}
                  title={
                    isDisabled
                      ? (decision?.unavailable_reason ?? undefined)
                      : undefined
                  }
                  onClick={() => {
                    if (isDisabled) return;
                    onSelectOption(opt.uuid);
                  }}
                  className={cn(
                    "h-auto rounded-[4px] border-[0.5px] px-3 py-1.5 text-[12px] font-light transition-all duration-150",
                    isDisabled
                      ? "cursor-not-allowed border-dashed border-[var(--rule)] bg-[var(--bg-light)] text-[var(--ink-3)] line-through opacity-70"
                      : "cursor-pointer",
                    !isDisabled && isSelected
                      ? "border-[var(--green)] bg-[var(--green)] font-light text-white hover:bg-[var(--green-hover)]"
                      : !isDisabled
                        ? "border-[var(--rule)] bg-[var(--bg)] text-[var(--ink)] hover:border-[var(--green)] hover:text-[var(--green)]"
                        : "",
                  )}
                >
                  {label}
                </Button>
              );
            })}
          </div>
          {(() => {
            const blockedReasons = activeClass.options
              .map((opt) =>
                getOptionAvailability(availability, activeClass.uuid, opt.uuid),
              )
              .filter((d): d is AvailabilityResult => !!d && !d.available);
            if (blockedReasons.length === 0) return null;
            const uniqueMessages = Array.from(
              new Set(
                blockedReasons
                  .map((d) => d.unavailable_reason)
                  .filter((m): m is string => !!m),
              ),
            );
            if (uniqueMessages.length === 0) return null;
            return (
              <p className="mt-2 text-[11px] text-[var(--ink-3)]">
                Some options are unavailable: {uniqueMessages.join("; ")}
              </p>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Product Panel
// ---------------------------------------------------------------------------

export function ProductPanel({
  state,
  update,
  detectedCategory,
  metadata,
  showWarrantySelector = true,
  flatProductSelection = false,
  requiredError,
  productTemplateName,
}: {
  state: WizardState;
  update: (payload: Partial<WizardState>) => void;
  detectedCategory: string | null;
  metadata?: RegistrationFormMetadata | null;
  showWarrantySelector?: boolean;
  /**
   * When true, the panel skips the category-card grid entirely and
   * shows a flat product list. Selecting a parent product (one with
   * variants) drills into a variant picker rather than completing the
   * step. Standalone products complete the step normally.
   *
   * Drives Owens-Corning style flows where the product itself is the
   * primary entry point, not the category.
   */
  flatProductSelection?: boolean;
  /**
   * Required-field error from the wizard validation context. When
   * present and no product is selected, the panel displays an inline
   * red hint matching the rest of the wizard's error treatment.
   */
  requiredError?: string | null;
  /**
   * When set, the product list is narrowed to only products whose
   * `product_template.name` matches this string. Used when navigating
   * from a claim form that is tied to a specific product line (e.g.
   * "Basement Finishing System", "SunSuite 4-Season Room"). Null or
   * undefined means no filtering — all products are shown.
   */
  productTemplateName?: string | null;
}) {
  const { t } = useTranslation();
  // ``withProductCount`` asks the backend to annotate each row with the
  // number of registerable PUBLISHED products. Used by the picker's
  // category cards (e.g. "Pumps · 292 products") so we don't need to
  // pull the full product list to compute counts. Mandatory for
  // catalogues over the API page-size cap (10k+ rows) - without it,
  // category counts only reflect products that landed in the first
  // 100-row page. Pass the selected product type so the count matches
  // the filtered product list, not the whole category.
  const { data: rawCategories } = useProductCategories({
    withProductCount: true,
    templateName: productTemplateName,
  });
  const { data: productTemplates } = useProductTemplates({
    enabled: flatProductSelection,
  });

  // Flatten the category tree - show leaf categories for selection
  const categories = useMemo(() => {
    const leaves: typeof rawCategories = [];
    function walk(cats: typeof rawCategories) {
      for (const cat of cats ?? []) {
        if (cat.children?.length) {
          walk(cat.children);
        } else {
          leaves.push(cat);
        }
      }
    }
    walk(rawCategories);
    return leaves.length > 0 ? leaves : rawCategories;
  }, [rawCategories]);

  const [selectedCategoryUuid, setSelectedCategoryUuid] = useState<
    string | null
  >(null);
  const [search, setSearch] = useState("");
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  // Global product search - always-visible quick-find at the top of step 1.
  // Hits /products/items/?search= which performs a multi-field icontains
  // across cached_short_name, cached_long_name, product_template.name,
  // category.name, and the product_data JSON (so model number, SKU, brand,
  // and every dynamic attribute are searchable). Debounced 300ms so we
  // don't hammer the API on every keystroke.
  const [globalSearch, setGlobalSearch] = useState("");
  const [debouncedGlobalSearch, setDebouncedGlobalSearch] = useState("");
  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedGlobalSearch(globalSearch),
      300,
    );
    return () => window.clearTimeout(handle);
  }, [globalSearch]);
  const { data: globalSearchResults, isLoading: globalSearchLoading } =
    useProductSearch(debouncedGlobalSearch, { include_variants: false });
  const { data: holdingEntity } = useEntityTypeByKey("holding");
  const stockIdentityLabels = useMemo(
    () => stockBackedIdentityLabels(holdingEntity),
    [holdingEntity],
  );
  const { data: stockResolve, isFetching: stockResolveFetching } =
    useStockProductResolve(
      debouncedGlobalSearch,
      stockIdentityLabels.length > 0,
    );
  // Only a settled answer may lock the product; a cached one could say
  // "available" for a value someone has since registered.
  const uniqueStockMatch =
    !stockResolveFetching &&
    stockResolve?.exact &&
    stockResolve.results.length === 1
      ? stockResolve.results[0]
      : undefined;
  const availableStockMatch = lockableStockMatch(uniqueStockMatch);
  const takenStockMatch = isTakenStockMatch(uniqueStockMatch);
  const showGlobalSearchResults =
    debouncedGlobalSearch.trim().length >= 2 &&
    !availableStockMatch &&
    !takenStockMatch;

  useEffect(() => {
    if (!availableStockMatch?.product) return;
    if (
      state.productLockedByStock &&
      state.selectedProduct?.uuid === availableStockMatch.product.uuid
    ) {
      return;
    }
    update({
      selectedProduct: availableStockMatch.product,
      productLockedByStock: true,
      stockResolvedAttribute: availableStockMatch.attribute,
      dynamicAttributes: {
        ...state.dynamicAttributes,
        [availableStockMatch.attribute]: wrapStockFormValue(
          availableStockMatch.value,
        ),
      },
    });
    setGlobalSearch("");
  }, [
    availableStockMatch,
    state.productLockedByStock,
    state.selectedProduct?.uuid,
    state.dynamicAttributes,
    update,
  ]);

  const clearProductSelection = useCallback(() => {
    update({
      selectedProduct: null,
      productLockedByStock: false,
      stockResolvedAttribute: null,
      dynamicAttributes: {},
      warrantyTemplateUuid: null,
      warrantyTemplateOptionUuid: null,
    });
    setGlobalSearch("");
  }, [update]);
  // Flat-mode only: the parent VARIANT_BASE the user has drilled into.
  // When set, the panel renders the variant picker instead of the
  // top-level product list.
  const [parentBrowsingUuid, setParentBrowsingUuid] = useState<string | null>(
    null,
  );

  // Auto-select category from detected type (categorised mode only)
  if (
    !flatProductSelection &&
    detectedCategory &&
    categories &&
    !hasAutoSelected
  ) {
    const match = categories.find(
      (c) =>
        c.slug === detectedCategory ||
        c.name.toLowerCase().includes(detectedCategory.replace("-", " ")),
    );
    if (match) {
      setSelectedCategoryUuid(match.uuid);
      setHasAutoSelected(true);
    }
  }

  // Host attribute values committed by the host-context step. Forwarded as
  // `host_attrs` to the wizard's product fetches so the backend can apply
  // global `AvailabilityRule(scope='product')` narrowing (e.g. structure
  // type → category gating). Geo-eligible flows already get this narrowing
  // applied by `/geo/eligibility/` and short-circuit the API path below.
  const wizardHostAttrs = state.hostAttributeValues ?? null;

  // Resolve postal code from host attribute values via the metadata's
  // host template - same `maps_to_column === 'address_postcode'` lookup
  // the host_context step uses. Combined with `installation_date` from
  // early attributes, these drive PSA + availability-window narrowing on
  // the products endpoint, so wizard flows that never run the dedicated
  // `/geo/eligibility/` step still respect Plant Service Area rules.
  const wizardPostalCode = useMemo<string | null>(() => {
    const attrs = metadata?.host_template?.attributes;
    if (!attrs || !wizardHostAttrs) return null;
    const postcodeAttr = attrs.find(
      (a) => a.maps_to_column === "address_postcode",
    );
    const raw = postcodeAttr
      ? wizardHostAttrs[postcodeAttr.code_name]
      : undefined;
    return raw ? String(raw) : null;
  }, [metadata?.host_template?.attributes, wizardHostAttrs]);
  const wizardReferenceDate = useMemo<string | null>(() => {
    const raw = state.earlyAttributes?.installation_date;
    return raw ? String(raw) : null;
  }, [state.earlyAttributes?.installation_date]);

  const eligibility = state.geoEligibility;
  const productCountByCategory = useMemo(() => {
    // When the host_context step has produced an eligibility result -
    // regardless of whether a Plant Service Area was matched - that
    // result is the authoritative source of "what's available for this
    // address on the chosen installation date". Use it for the per-
    // category counts even when it contains zero rows; falling back to
    // the global counts here would contradict the empty product lists
    // the picker renders below (and the "we couldn't match this address
    // to a PSA" banner shown above it).
    if (eligibility) {
      const counts: Record<string, number> = {};
      for (const p of eligibility.products ?? []) {
        const catUuid = p.category?.uuid;
        if (catUuid) counts[catUuid] = (counts[catUuid] ?? 0) + 1;
      }
      return counts;
    }
    // Default path: read counts directly off the categories list, which
    // was annotated server-side via ``annotate_product_count=true``. No
    // need to pull the full product set just to compute totals.
    const counts: Record<string, number> = {};
    for (const cat of rawCategories ?? []) {
      counts[cat.uuid] = cat.product_count ?? 0;
    }
    return counts;
  }, [rawCategories, eligibility]);

  // PSA + availability-window narrowing.
  //
  // When the host_context step has produced an eligibility result the
  // wizard treats it as authoritative - including the case where the
  // address could not be matched to any active Plant Service Area
  // (``psa === null``). The eligibility API has already intersected
  // ``ProductAvailability`` (PSA + active window for the chosen
  // installation date) with every matched global product filter rule,
  // so its product list is exactly what the user is allowed to register.
  // Falling back to the unfiltered ``/products/items/`` call here would
  // silently re-expose the global catalogue and contradict the "we
  // couldn't match this address to a PSA" banner shown above the picker.
  const geoProducts = state.geoEligibility?.products as Product[] | undefined;
  const psaResolved = !!state.geoEligibility?.psa;
  const hasGeoFilter = !!state.geoEligibility;
  // When the scoped product template opts out of PSA (geo) filtering the
  // returned list is the template's catalogue, not a geo-narrowed set - so
  // ``hasGeoFilter`` still drives the product source, but the PSA-specific
  // empty-state copy below must fall back to a generic message.
  const psaFilteringApplied =
    state.geoEligibility?.psaFilteringApplied !== false;

  const { data: productsData, isLoading: apiLoading } = usePublishedProducts(
    hasGeoFilter
      ? undefined
      : {
          // In flat mode we ignore category filtering entirely; the user
          // browses the full product catalogue at once.
          category_uuid: flatProductSelection ? null : selectedCategoryUuid,
          search,
          // Cache only the bounded first page (300) of the catalogue
          // instead of pulling every SKU (was 1000). This is what the
          // post-login warmer prewarms, so the picker paints instantly
          // from cache on wizard open. Categories over 300 products are
          // reached via the server-side search box below the picker.
          page_size: "300",
          include_variants: true,
          host_attrs: wizardHostAttrs,
          postal_code: wizardPostalCode,
          reference_date: wizardReferenceDate,
          product_template_name: productTemplateName,
        },
  );
  const isLoading = hasGeoFilter ? false : apiLoading;

  // Flat-mode variant fetch - only enabled when the user has drilled
  // into a parent product. We rely on the dedicated `parent_product_uuid`
  // filter so the response is exactly the variant set we need.
  const { data: variantData, isLoading: variantsLoading } =
    usePublishedProducts(
      parentBrowsingUuid && !hasGeoFilter
        ? {
            parent_product_uuid: parentBrowsingUuid,
            search,
            page_size: "100",
            host_attrs: wizardHostAttrs,
            postal_code: wizardPostalCode,
            reference_date: wizardReferenceDate,
          }
        : undefined,
    );

  // Unfiltered base counts for the picker contexts. Used to decide
  // whether the search bar appears (small catalogues don't need it) -
  // the count source must NOT shrink as the user types or the search
  // bar disappears mid-search. ``geoProducts`` is already
  // client-filtered, so it serves as the unfiltered base for
  // geo-eligible flows. Non-geo flows derive counts from the
  // server-annotated ``rawCategories[*].product_count`` so we don't
  // need to fetch the full product list.
  const PRODUCT_SEARCH_MIN_COUNT = 20;
  const baseFlatProductCount = useMemo(() => {
    if (hasGeoFilter) {
      let products = (geoProducts ?? []).filter(
        (p) => p.product_type !== "VARIANT",
      );
      products = products.filter(
        (p) => p.product_type !== "VARIANT_BASE" || (p.variant_count ?? 0) > 0,
      );
      return products.length;
    }
    return (rawCategories ?? []).reduce(
      (sum, cat) => sum + (cat.product_count ?? 0),
      0,
    );
  }, [hasGeoFilter, geoProducts, rawCategories]);
  const baseCategoryProductCount = useMemo(() => {
    if (!selectedCategoryUuid) return 0;
    if (hasGeoFilter) {
      return (geoProducts ?? []).filter(
        (p) =>
          p.category?.uuid === selectedCategoryUuid &&
          p.product_type !== "VARIANT_BASE",
      ).length;
    }
    const cat = (rawCategories ?? []).find(
      (c) => c.uuid === selectedCategoryUuid,
    );
    return cat?.product_count ?? 0;
  }, [hasGeoFilter, geoProducts, rawCategories, selectedCategoryUuid]);
  const baseVariantCount = useMemo(() => {
    if (!parentBrowsingUuid) return 0;
    if (hasGeoFilter) {
      return (geoProducts ?? []).filter(
        (p) =>
          p.product_type === "VARIANT" &&
          p.parent_product_uuid === parentBrowsingUuid,
      ).length;
    }
    // Variant fetch's paginated total - already filtered to this parent.
    return variantData?.count ?? 0;
  }, [hasGeoFilter, geoProducts, parentBrowsingUuid, variantData?.count]);
  const showFlatProductSearch = baseFlatProductCount > PRODUCT_SEARCH_MIN_COUNT;
  const showCategoryProductSearch =
    baseCategoryProductCount > PRODUCT_SEARCH_MIN_COUNT;
  const showVariantSearch = baseVariantCount > PRODUCT_SEARCH_MIN_COUNT;

  // Drop any stale search term when the active picker no longer renders a
  // search bar - otherwise it would silently filter the API call for the
  // small list and leave the user staring at an empty result set with no
  // input to clear.
  const activePickerHasSearch = parentBrowsingUuid
    ? showVariantSearch
    : flatProductSelection
      ? showFlatProductSearch
      : selectedCategoryUuid
        ? showCategoryProductSearch
        : false;
  useEffect(() => {
    if (!activePickerHasSearch && search) {
      const handle = window.setTimeout(() => setSearch(""), 0);
      return () => window.clearTimeout(handle);
    }
  }, [activePickerHasSearch, search]);

  const filteredProducts = useMemo(() => {
    let products: Product[];
    if (hasGeoFilter) {
      products = [...(geoProducts ?? [])];
      if (!flatProductSelection && selectedCategoryUuid) {
        products = products.filter(
          (p) => p.category?.uuid === selectedCategoryUuid,
        );
      }
      if (search && search.length >= 2) {
        const q = search.toLowerCase();
        products = products.filter((p) =>
          (p.cached_short_name ?? "").toLowerCase().includes(q),
        );
      }
    } else {
      products = productsData?.results ?? [];
    }

    // Flat mode shows parent rows (PRODUCT + VARIANT_BASE) at the top
    // level; variants are reached via drilldown. Categorised mode is
    // unchanged: variants are surfaced directly and VARIANT_BASE rows
    // are hidden because their children are listed alongside them.
    if (flatProductSelection) {
      products = products.filter((p) => p.product_type !== "VARIANT");
      // Hide VARIANT_BASE parents that have no variants - they would
      // render an unselectable "0 variants" row that drills into an
      // empty list. Standalone PRODUCT rows are kept as-is.
      products = products.filter(
        (p) => p.product_type !== "VARIANT_BASE" || (p.variant_count ?? 0) > 0,
      );
    } else {
      products = products.filter((p) => p.product_type !== "VARIANT_BASE");
    }

    // When a product template name is provided (e.g. navigated from a
    // BFS or SunSuite claim form), narrow to only products from that
    // template so the picker doesn't show the entire OC catalogue.
    if (productTemplateName) {
      products = products.filter(
        (p) => p.product_template?.name === productTemplateName,
      );
    } else {
      // Standalone entry: hide templates provisioned through their claim flow
      // (e.g. BFS). They stay reachable via the claim round-trip, which passes
      // an explicit productTemplateName and so takes the branch above.
      products = products.filter(
        (p) => !p.product_template?.registration_via_claim_only,
      );
    }

    return products.sort((a, b) =>
      (a.cached_short_name ?? "").localeCompare(b.cached_short_name ?? ""),
    );
  }, [
    hasGeoFilter,
    geoProducts,
    productsData?.results,
    selectedCategoryUuid,
    search,
    flatProductSelection,
    productTemplateName,
  ]);

  // Categorised mode: group variants under their base product so the list
  // reads like the Product Library (base heading + its variants) instead of a
  // flat variant dump. Standalone products (no parent) stay as headerless
  // rows. Ordering, geo-eligibility and search are inherited from
  // `filteredProducts`; base names resolve from the VARIANT_BASE rows in the
  // raw product list (or the variant's parent_product) so the heading reads
  // "ACE Heavy Duty" rather than a size-prefixed variant name.
  const groupedProducts = useMemo(() => {
    const rawProducts = hasGeoFilter
      ? (geoProducts ?? [])
      : (productsData?.results ?? []);
    const nameByUuid = new Map<string, string>();
    for (const p of rawProducts) {
      if (p.cached_short_name) nameByUuid.set(p.uuid, p.cached_short_name);
    }

    const baseMap = new Map<
      string,
      { key: string; baseName: string | null; products: Product[] }
    >();
    const groups: Array<{
      key: string;
      baseName: string | null;
      products: Product[];
    }> = [];

    for (const p of filteredProducts) {
      const parentUuid =
        p.product_type === "VARIANT" ? (p.parent_product_uuid ?? null) : null;
      if (parentUuid) {
        let entry = baseMap.get(parentUuid);
        if (!entry) {
          entry = {
            key: parentUuid,
            baseName:
              nameByUuid.get(parentUuid) ??
              p.parent_product?.cached_short_name ??
              "Other",
            products: [],
          };
          baseMap.set(parentUuid, entry);
          groups.push(entry);
        }
        entry.products.push(p);
      } else {
        groups.push({ key: p.uuid, baseName: null, products: [p] });
      }
    }

    return groups.sort((a, b) => {
      const an = a.baseName ?? a.products[0]?.cached_short_name ?? "";
      const bn = b.baseName ?? b.products[0]?.cached_short_name ?? "";
      return an.localeCompare(bn);
    });
  }, [filteredProducts, hasGeoFilter, geoProducts, productsData?.results]);

  // Variants for the currently drilled-into parent. Parent-browsing is
  // reachable from the always-on global search in BOTH flat and
  // categorised modes, so this is gated only on the drilled-into parent.
  const variantProducts = useMemo<Product[]>(() => {
    if (!parentBrowsingUuid) return [];
    let variants: Product[];
    if (hasGeoFilter) {
      variants = (geoProducts ?? []).filter(
        (p) =>
          p.product_type === "VARIANT" &&
          p.parent_product_uuid === parentBrowsingUuid,
      );
      if (search && search.length >= 2) {
        const q = search.toLowerCase();
        variants = variants.filter((p) =>
          (p.cached_short_name ?? "").toLowerCase().includes(q),
        );
      }
    } else {
      variants = variantData?.results ?? [];
    }
    return variants.sort((a, b) =>
      (a.cached_short_name ?? "").localeCompare(b.cached_short_name ?? ""),
    );
  }, [
    parentBrowsingUuid,
    hasGeoFilter,
    geoProducts,
    variantData?.results,
    search,
  ]);

  // Resolve the parent product display object for the drilldown header
  // without an extra fetch - it's already in the top-level list.
  const parentProduct = useMemo<Product | null>(() => {
    if (!parentBrowsingUuid) return null;
    const all = hasGeoFilter
      ? (geoProducts ?? [])
      : (productsData?.results ?? []);
    return all.find((p) => p.uuid === parentBrowsingUuid) ?? null;
  }, [parentBrowsingUuid, hasGeoFilter, geoProducts, productsData?.results]);

  // Variant axis label - derived from the placeholders used in the
  // template's `variant_short_name_format` (e.g. "{color}" → "color",
  // "{color} / {size}" → "color & size"). This ties the selector label
  // to the same string that already drives each variant row's display
  // name, so flipping the format is enough to re-label both without a
  // schema change or a per-attribute flag.
  const { data: parentTemplate } = useProductTemplate(
    parentProduct?.product_template.uuid ?? null,
  );
  const variantAxisLabelByTemplateUuid = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const template of productTemplates ?? []) {
      labels[template.uuid] = getVariantAxisLabelFromTemplate(template);
    }
    return labels;
  }, [productTemplates]);
  const variantAxisLabel = useMemo(() => {
    return getVariantAxisLabelFromTemplate(parentTemplate);
  }, [parentTemplate]);
  const variantAxisLabelLower = variantAxisLabel.toLowerCase();

  // Wrap parent-browsing transitions so they always clear the search
  // box. Without this, a stale parent-list query (e.g. "Three Tab")
  // would survive the drilldown and hide every variant on entry.
  const enterParentBrowsing = (uuid: string) => {
    setSearch("");
    setParentBrowsingUuid(uuid);
  };
  const exitParentBrowsing = () => {
    setSearch("");
    setParentBrowsingUuid(null);
  };

  // Extended Warranty role gate - hide EXTENDED_WARRANTY templates
  // for users without `warranties.register_extended` permission (compliance-grade, $40K+ liability)
  const { can } = usePermissions();
  const canRegisterExtended = can("warranties.register_extended");

  const visibleWarrantyTemplates = useMemo(() => {
    const templates = metadata?.warranty_templates ?? [];
    if (canRegisterExtended) return templates;
    return templates.filter((wt) => wt.warranty_type !== "EXTENDED_WARRANTY");
  }, [metadata?.warranty_templates, canRegisterExtended]);

  useEffect(() => {
    const activeWarranty = visibleWarrantyTemplates.find(
      (template) => template.uuid === state.warrantyTemplateUuid,
    );

    if (!activeWarranty) return;

    const defaultOptionUuid = getDefaultWarrantyOptionUuid(activeWarranty);
    if (
      defaultOptionUuid &&
      state.warrantyTemplateOptionUuid !== defaultOptionUuid
    ) {
      update({ warrantyTemplateOptionUuid: defaultOptionUuid });
      return;
    }

    if (
      activeWarranty.options.length === 0 &&
      state.warrantyTemplateOptionUuid
    ) {
      update({ warrantyTemplateOptionUuid: null });
      return;
    }

    if (
      state.warrantyTemplateOptionUuid &&
      !activeWarranty.options.some(
        (option) => option.uuid === state.warrantyTemplateOptionUuid,
      )
    ) {
      update({ warrantyTemplateOptionUuid: null });
    }
  }, [
    visibleWarrantyTemplates,
    state.warrantyTemplateUuid,
    state.warrantyTemplateOptionUuid,
    update,
  ]);

  const hasSelection = !!state.selectedProduct;

  // After selection: show the original Product card with dropdown + warranty.
  // A stock-resolved serial locks product + model so they cannot be edited.
  if (hasSelection) {
    const lockedLabels = state.productLockedByStock
      ? lockedCatalogueLabels(state.selectedProduct!)
      : null;
    return (
      <RegistrationPanel
        title="Product"
        icon={<Package size={14} className="text-[var(--ink-3)]" />}
        className="transition-all duration-150"
      >
        {lockedLabels ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <p className="text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
                {t("wizard.product", "Product")}
              </p>
              <p className="text-[13px] text-[var(--ink)]">
                {lockedLabels.productName}
              </p>
            </div>
            {lockedLabels.modelName ? (
              <div className="flex flex-col gap-1">
                <p className="text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  {t("wizard.model", "Model")}
                </p>
                <p className="text-[13px] text-[var(--ink)]">
                  {lockedLabels.modelName}
                </p>
              </div>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearProductSelection}
              className="self-start px-0 text-[12px] font-light text-[var(--ink-3)]"
            >
              {t("wizard.searchAgain", "Search again")}
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            type="button"
            onClick={clearProductSelection}
            className="flex h-9 w-full cursor-pointer items-center justify-between rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 text-left font-light"
          >
            <span className="truncate text-[13px] text-[var(--ink)]">
              {state.selectedProduct!.cached_short_name}
            </span>
            <ChevronDown size={14} className="shrink-0 text-[var(--ink-3)]" />
          </Button>
        )}
        {showWarrantySelector && visibleWarrantyTemplates.length > 0 && (
          <WarrantySelector
            warrantyTemplates={visibleWarrantyTemplates}
            selectedClassUuid={state.warrantyTemplateUuid}
            selectedOptionUuid={state.warrantyTemplateOptionUuid}
            onSelectClass={(uuid) => {
              const nextWarranty = visibleWarrantyTemplates.find(
                (template) => template.uuid === uuid,
              );
              update({
                warrantyTemplateUuid: uuid,
                warrantyTemplateOptionUuid:
                  getDefaultWarrantyOptionUuid(nextWarranty),
              });
            }}
            onSelectOption={(uuid) =>
              update({ warrantyTemplateOptionUuid: uuid })
            }
          />
        )}
      </RegistrationPanel>
    );
  }

  // ── Flat-mode top-level, OR the variant drilldown in any mode ──────
  // A parent picked from the always-on global search must show its
  // variants even in categorised mode, so the drilldown renders whenever
  // a parent is being browsed - regardless of flatProductSelection.
  if (flatProductSelection || parentBrowsingUuid) {
    // Drilldown view: parent VARIANT_BASE selected, list its variants.
    if (parentBrowsingUuid) {
      return (
        <div className="panel-arrive flex flex-col gap-4">
          <p className="text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
            Product
          </p>
          <div className="flex flex-col rounded-[8px] overflow-hidden border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
            {/* Header: back + parent context */}
            <div className="flex items-center gap-2 border-b-[0.5px] border-[var(--rule)] px-3 py-2.5">
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={exitParentBrowsing}
                aria-label="Back to all products"
                className="-ml-1 h-7 w-7 shrink-0 text-[var(--ink-3)] hover:text-[var(--ink)]"
              >
                <ArrowLeft size={14} />
              </Button>
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <Package size={12} className="shrink-0 text-[var(--ink-3)]" />
                <button
                  type="button"
                  onClick={exitParentBrowsing}
                  className="truncate text-[12px] text-[var(--ink-3)] hover:text-[var(--ink)] cursor-pointer"
                >
                  All products
                </button>
                <ChevronRight
                  size={11}
                  className="shrink-0 text-[var(--ink-4)]"
                />
                <span className="truncate text-[12px] font-light text-[var(--ink)]">
                  {parentProduct?.cached_short_name ?? "Variants"}
                </span>
              </div>
            </div>

            {/* Sub-label */}
            <p className="border-b-[0.5px] border-[var(--rule)] px-4 py-2.5 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
              Select a {variantAxisLabelLower}
            </p>

            {/* Search - hidden for short variant lists where browsing is faster */}
            {showVariantSearch && (
              <div className="border-b-[0.5px] border-[var(--rule)] px-4 py-2.5">
                <SearchInput
                  placeholder={`Search ${variantAxisLabelLower}s...`}
                  value={search}
                  onChange={setSearch}
                  className="h-8 text-[13px]"
                />
              </div>
            )}

            <div className="clara-scroll max-h-[320px] overflow-y-auto">
              {variantsLoading ? (
                <div className="flex flex-col">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 border-b-[0.5px] border-[var(--rule)] px-4 py-3.5 last:border-b-0"
                    >
                      <div className="h-[15px] w-[15px] shrink-0 rounded-full bg-[var(--skeleton-base)]" />
                      <div className="h-[13px] w-[55%] rounded-[2px] bg-[var(--skeleton-base)]" />
                    </div>
                  ))}
                </div>
              ) : variantProducts.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <p className="text-[13px] text-[var(--ink-3)]">
                    {hasGeoFilter && !search
                      ? !psaFilteringApplied
                        ? `No variants are available for this product.`
                        : psaResolved
                          ? `No variants are available in ${state.geoEligibility?.psa?.name ?? "this Plant Service Area"} on the chosen installation date.`
                          : `No variants are available - this address didn\u2019t match a Plant Service Area.`
                      : `No variants found${search ? ` matching \u201c${search}\u201d` : ""}`}
                  </p>
                </div>
              ) : (
                variantProducts.map((variant, i) => (
                  <ProductRow
                    key={variant.uuid}
                    product={variant}
                    selected={state.selectedProduct?.uuid === variant.uuid}
                    hasSelection={hasSelection}
                    onSelect={() => update({ selectedProduct: variant })}
                    isLast={i === variantProducts.length - 1}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      );
    }

    // Top-level flat product list.
    return (
      <div className="flex flex-col gap-4">
        <p className="text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
          Product
        </p>
        {requiredError && (
          <p className="-mt-2 text-xs text-[var(--status-error)]">
            {requiredError}
          </p>
        )}
        <div
          className={cn(
            "flex flex-col rounded-[8px] border-[0.5px] bg-[var(--bg-subtle)]",
            requiredError ? "border-destructive" : "border-[var(--rule)]",
          )}
        >
          {/* Search - hidden for short product lists where browsing is faster */}
          {showFlatProductSearch && (
            <div className="border-b-[0.5px] border-[var(--rule)] px-4 py-2.5">
              <SearchInput
                placeholder="Search products..."
                value={search}
                onChange={setSearch}
                className="h-8 text-[13px]"
              />
            </div>
          )}

          <div className="clara-scroll max-h-[420px] overflow-y-auto">
            {isLoading ? (
              <div className="flex flex-col">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 border-b-[0.5px] border-[var(--rule)] px-4 py-3.5 last:border-b-0"
                  >
                    <div className="h-[15px] w-[15px] shrink-0 rounded-full bg-[var(--skeleton-base)]" />
                    <div className="h-[13px] w-[60%] rounded-[2px] bg-[var(--skeleton-base)]" />
                    <div className="ml-auto h-[18px] w-[64px] rounded-[3px] bg-[var(--skeleton-base)]" />
                  </div>
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <p className="text-[13px] text-[var(--ink-3)]">
                  {hasGeoFilter && !search
                    ? !psaFilteringApplied
                      ? `No products are available for this selection.`
                      : psaResolved
                        ? `No products are available in ${state.geoEligibility?.psa?.name ?? "this Plant Service Area"} on the chosen installation date.`
                        : `No products are available - this address didn\u2019t match a Plant Service Area.`
                    : `No products found${search ? ` matching \u201c${search}\u201d` : ""}`}
                </p>
              </div>
            ) : (
              filteredProducts.map((product, i) => (
                <FlatProductRow
                  key={product.uuid}
                  product={product}
                  variantAxisLabel={
                    variantAxisLabelByTemplateUuid[
                      product.product_template.uuid
                    ]
                  }
                  selected={state.selectedProduct?.uuid === product.uuid}
                  onSelect={() => {
                    if (
                      product.product_type === "VARIANT_BASE" ||
                      (product.variant_count ?? 0) > 0
                    ) {
                      enterParentBrowsing(product.uuid);
                    } else {
                      update({ selectedProduct: product });
                    }
                  }}
                  isLast={i === filteredProducts.length - 1}
                />
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Categorised mode (default) ────────────────────────────────────
  const catalogueHits = globalSearchResults?.results ?? [];
  const stockHits = (stockResolve?.results ?? [])
    .filter((row) => isAvailableStockStatus(row.status))
    .map((row) => row.product)
    .filter((product): product is Product => !!product);
  const globalSearchHits = [...stockHits, ...catalogueHits].filter(
    (product, index, all) =>
      all.findIndex((other) => other.uuid === product.uuid) === index,
  );
  const globalSearchPending = globalSearchLoading || stockResolveFetching;
  return (
    // ProductPanel sits in the left column of the wizard's 2-col
    // RegistrationStepGrid (lg:grid-cols-2). When a category is selected
    // the picker needs the full content width, so span both columns -
    // the inner 50/50 category|picker split then fills the page's
    // content width (within the header tramlines) instead of being
    // squeezed into the left half-column. Browsing (no category) stays
    // in its normal column. col-span only affects grid items, so this is
    // a no-op in any non-grid placement.
    <div
      className={cn(
        "flex flex-col gap-4",
        selectedCategoryUuid && "lg:col-span-2",
      )}
    >
      {/* Always-visible global product search - quick-find by model
          number, SKU, brand, name, or any dynamic attribute. Configured
          tenants with categories also keep their category navigation
          below; this is purely additive. Constrained to the left half
          once a category is selected so it lines up with the category
          column rather than spanning the whole page. */}
      <div
        className={cn(
          "flex flex-col gap-2",
          selectedCategoryUuid && "lg:max-w-[calc(50%-8px)]",
        )}
      >
        <p
          className={cn(
            "text-[10px] font-light uppercase tracking-[0.14em]",
            takenStockMatch
              ? "text-[var(--status-error)]"
              : "text-[var(--ink-3)]",
          )}
        >
          {t("wizard.findAProduct", "Find a product")}
        </p>
        <SearchInput
          value={globalSearch}
          onChange={setGlobalSearch}
          aria-invalid={takenStockMatch || undefined}
          className={
            takenStockMatch
              ? "ring-1 ring-[var(--status-error)]"
              : undefined
          }
          placeholder={
            stockIdentityLabels.length > 0
              ? t(
                  "wizard.searchProductsHintWithIdentity",
                  "Search by {{identity}}, model number, SKU, brand, or name...",
                  { identity: stockIdentityLabels.join(", ") },
                )
              : t(
                  "wizard.searchProductsHint",
                  "Search by model number, SKU, brand, or name...",
                )
          }
        />
        {takenStockMatch ? (
          <p
            role="alert"
            className="text-xs text-[var(--status-error)]"
          >
            {t(
              "wizard.stockValueNotAvailable",
              "This value is not available.",
            )}
          </p>
        ) : null}
        {showGlobalSearchResults && (
          <div className="panel-arrive flex flex-col rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
            {globalSearchPending ? (
              <p className="px-4 py-6 text-center text-[13px] text-[var(--ink-3)]">
                Searching…
              </p>
            ) : globalSearchHits.length === 0 ? (
              <p className="px-4 py-6 text-center text-[13px] text-[var(--ink-3)]">
                No products found matching &ldquo;{debouncedGlobalSearch}&rdquo;
              </p>
            ) : (
              <>
                <p className="border-b-[0.5px] border-[var(--rule)] px-4 py-2 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  {globalSearchHits.length === 1
                    ? "1 match"
                    : `${globalSearchHits.length} matches`}
                </p>
                {globalSearchHits.map((product, i) => (
                  <FlatProductRow
                    key={product.uuid}
                    product={product}
                    variantAxisLabel={
                      variantAxisLabelByTemplateUuid[
                        product.product_template.uuid
                      ]
                    }
                    selected={state.selectedProduct?.uuid === product.uuid}
                    onSelect={() => {
                      if (
                        product.product_type === "VARIANT_BASE" ||
                        (product.variant_count ?? 0) > 0
                      ) {
                        enterParentBrowsing(product.uuid);
                      } else {
                        update({ selectedProduct: product });
                      }
                      setGlobalSearch("");
                    }}
                    isLast={i === globalSearchHits.length - 1}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </div>
      {/* Phase label */}
      <p className="text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {categories && categories.length > 0
          ? "Or browse by category"
          : "Product category"}
      </p>
      {requiredError && (
        <p className="-mt-2 text-xs text-[var(--status-error)]">
          {requiredError}
        </p>
      )}
      {/* Empty-state hint when no categories are configured for this
          tenant - the global search above is still fully usable. */}
      {(!categories || categories.length === 0) && !requiredError && (
        <p className="text-[13px] text-[var(--ink-3)]">
          No product categories configured yet - use the search above to find a
          product.
        </p>
      )}
      {/* Category + product two-pane layout.
       *
       * Browse-only (no category selected): category cards fill the
       * full width in a 3-column grid.
       *
       * Drill-down (a category is selected): the layout splits into a
       * two-column grid - categories stay at 3 cols in the left half
       * (cards narrow but the 3-col rhythm is preserved), the chosen
       * category's product picker fills the right half. */}
      {categories && categories.length > 0 && (
        <div
          className={cn(
            "grid gap-4",
            // When a category is selected, break out of the centered
            // 1200px page-container to the full page width and split it
            // 50/50: categories (3 across) fill the LEFT half, the
            // product picker fills the RIGHT half. main.page-container
            // has overflow-x:clip so the full-bleed margins introduce no
            // horizontal scroll. On narrow screens it stays a simple
            // in-container split.
            selectedCategoryUuid && "md:grid-cols-2",
          )}
        >
          <div
            className={cn(
              // Fixed-height, internally-scrolling frame. Tenants with
              // long catalogues (Pool Systems has 100+ categories) would
              // otherwise tower the page into an endless grid with the
              // product picker stranded at the top. Capping the height
              // and scrolling within this white-box frame keeps it tidy
              // and pairs cleanly with the product-picker pane (same
              // max-height) when a category is selected. Always three
              // columns: full width while browsing, and three across the
              // LEFT half once a category is selected and the layout
              // splits 50/50. No dark fill behind the cards - the frame
              // is just a hairline border + the page background, so the
              // white category cards read cleanly.
              "clara-scroll grid max-h-[460px] grid-cols-3 content-start gap-2.5 overflow-y-auto rounded-[8px] border-[0.5px] border-[var(--rule)] p-3",
            )}
          >
            {[...categories]
              .sort(
                (a, b) =>
                  (productCountByCategory[b.uuid] ?? 0) -
                  (productCountByCategory[a.uuid] ?? 0),
              )
              .map((cat) => (
                <CategoryCard
                  key={cat.uuid}
                  label={cat.name}
                  productCount={productCountByCategory[cat.uuid] ?? 0}
                  icon={
                    <CategoryIcon
                      icon={cat.icon || ""}
                      className="text-[var(--green)]"
                    />
                  }
                  active={selectedCategoryUuid === cat.uuid}
                  onClick={() =>
                    setSelectedCategoryUuid(
                      selectedCategoryUuid === cat.uuid ? null : cat.uuid,
                    )
                  }
                />
              ))}
          </div>
          {selectedCategoryUuid && (
            // Light picker pane: a hairline-bordered frame on the page
            // background (no dark fill), mirroring the category frame on
            // the left. The header strip and the product search row read
            // light - the search bar matches the main "Find a product"
            // search above - and the white product rows sit cleanly on top.
            <div className="panel-arrive flex max-h-[460px] flex-col rounded-[8px] border-[0.5px] border-[var(--rule)]">
              <p className="border-b-[0.5px] border-[var(--rule)] px-4 py-3 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
                {categories?.find((c) => c.uuid === selectedCategoryUuid)?.name}{" "}
                - select a product
              </p>

              {/* Search - hidden for short product lists where browsing is faster */}
              {showCategoryProductSearch && (
                <div className="border-b-[0.5px] border-[var(--rule)] px-4 py-2.5">
                  <SearchInput
                    placeholder="Search products..."
                    value={search}
                    onChange={setSearch}
                    className="h-8 text-[13px]"
                  />
                </div>
              )}

              <div className="clara-scroll flex-1 overflow-y-auto">
                {isLoading ? (
                  /* Skeleton rows */
                  <div className="flex flex-col">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 border-b-[0.5px] border-[var(--rule)] px-4 py-3.5 last:border-b-0"
                      >
                        <div className="h-[13px] w-[60%] rounded-[2px] bg-[var(--skeleton-base)]" />
                        <div className="ml-auto h-[18px] w-[80px] rounded-[3px] bg-[var(--skeleton-base)]" />
                      </div>
                    ))}
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className="px-4 py-10 text-center">
                    <p className="text-[13px] text-[var(--ink-3)]">
                      {hasGeoFilter && !search
                        ? !psaFilteringApplied
                          ? `No products are available in this category for this selection.`
                          : psaResolved
                            ? `No products in this category are available in ${state.geoEligibility?.psa?.name ?? "this Plant Service Area"} on the chosen installation date.`
                            : `No products are available - this address didn\u2019t match a Plant Service Area.`
                        : `No products found${search ? ` matching \u201c${search}\u201d` : ""}`}
                    </p>
                  </div>
                ) : (
                  groupedProducts.map((group, gi) => (
                    <div key={group.key}>
                      {group.baseName && (
                        <div className="flex items-center gap-2 border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-2">
                          <Package
                            size={11}
                            className="shrink-0 text-[var(--ink-3)]"
                          />
                          <span className="text-[12px] text-[var(--ink-2)]">
                            {group.baseName}
                          </span>
                          <span className="ml-auto text-[11px] font-light text-[var(--ink-4)]">
                            {group.products.length}
                          </span>
                        </div>
                      )}
                      {group.products.map((product, pi) => (
                        <ProductRow
                          key={product.uuid}
                          product={product}
                          selected={
                            state.selectedProduct?.uuid === product.uuid
                          }
                          hasSelection={hasSelection}
                          onSelect={() => update({ selectedProduct: product })}
                          isLast={
                            gi === groupedProducts.length - 1 &&
                            pi === group.products.length - 1
                          }
                        />
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Warranty Summary Panel (fills empty space below Product card)
// ---------------------------------------------------------------------------

export function WarrantySummaryPanel({
  metadata,
}: {
  metadata: RegistrationFormMetadata | null;
}) {
  if (!metadata) return null;

  const product = metadata.product;
  const warrantyTemplates = metadata.warranty_templates ?? [];
  if (warrantyTemplates.length === 0) return null;

  return (
    <div className="panel-arrive mt-4 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
      <p className="mb-3 text-[10px] font-light uppercase tracking-[0.16em] text-[var(--ink-3)]">
        Warranty Overview
      </p>
      {/* Product full name */}
      <p className="mb-3 text-[13px] text-[var(--ink)]">
        {product.cached_long_name || product.cached_short_name}
      </p>
      {/* Warranty classes */}
      <div className="flex flex-col gap-3">
        {warrantyTemplates.map((wc) => (
          <div
            key={wc.uuid}
            className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3.5 py-3"
          >
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[12px] font-light text-[var(--ink)]">
                {wc.name}
              </span>
              <span className="rounded-full bg-[var(--green-bg)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--green)]">
                {wc.warranty_type}
              </span>
            </div>
            {shouldShowWarrantyDuration(wc) && (
              <div className="flex flex-wrap gap-1.5">
                {wc.options.map((opt) => (
                  <span
                    key={opt.uuid}
                    className="rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-2 py-0.5 text-[11px] text-[var(--ink-3)]"
                  >
                    {opt.is_lifetime
                      ? "Lifetime"
                      : opt.display_name ||
                        formatDurationFromMonths(opt.duration_in_months)}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Additional Details Panel
// ---------------------------------------------------------------------------

// Host-level attributes are rendered by StepHostProduct - skip them in
// Additional Details to avoid duplication. The set is populated dynamically
// from the host template when present.
//
// All holding-level attributes are stored in state.dynamicAttributes[code_name].

type FieldMeta = RegistrationFormMetadata["fields"][number];

function useAutoSelectVehicleType(
  dynamicFields: FieldMeta[],
  state: WizardState,
  update: (payload: Partial<WizardState>) => void,
) {
  const categorySelectField = useMemo(
    () => dynamicFields.find((f) => f.code_name === "vehicle_type"),
    [dynamicFields],
  );
  useEffect(() => {
    if (!state.selectedProduct?.category?.name) return;
    if (!categorySelectField || categorySelectField.field_type !== "SELECT")
      return;
    const currentVal = state.dynamicAttributes?.[categorySelectField.code_name];
    if (currentVal) return;
    const options = (categorySelectField.options ?? []) as Array<{
      value: string;
      label: string;
    }>;
    const categoryName = state.selectedProduct.category.name.toLowerCase();
    const match = options.find(
      (o) =>
        o.label.toLowerCase() === categoryName ||
        o.value.toLowerCase() === categoryName,
    );
    if (match) {
      update({
        dynamicAttributes: {
          ...state.dynamicAttributes,
          [categorySelectField.code_name]: match.value,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedProduct?.uuid, categorySelectField]);
}

function DetailFieldsForm({
  formName,
  fields,
  state,
  update,
}: {
  formName: string;
  fields: FieldMeta[];
  state: WizardState;
  update: (payload: Partial<WizardState>) => void;
}) {
  const { t } = useTranslation();
  const updateRef = useRef(update);
  useEffect(() => {
    updateRef.current = update;
  });

  const dynamicAttrsRef = useRef(state.dynamicAttributes);
  useEffect(() => {
    dynamicAttrsRef.current = state.dynamicAttributes;
  });

  const productUuid = state.selectedProduct?.uuid;
  const fieldsMeta = useMemo<FormFieldMeta[]>(
    () =>
      fields.map((f) =>
        asStockSearchField(normaliseRegistrationField(f), productUuid, {
          placeholder: t(
            "registrations.valueStock.searchPlaceholder",
            "Search available values",
          ),
        }),
      ),
    [fields, productUuid, t],
  );

  const initialValues = useMemo(() => {
    const result: Record<string, unknown> = {};
    for (const meta of fieldsMeta) {
      const raw =
        state.dynamicAttributes?.[meta.code_name] ??
        meta.configuration_data?.default_value ??
        "";
      // Object-valued fields (ENTITY_SEARCH) validate as object|null, so an
      // unanswered optional field must carry null - a "" (the legacy empty
      // default, possibly persisted in a wizard draft) fails the generated
      // schema with "expected object, received string".
      if (meta.field_type === "ENTITY_SEARCH") {
        result[meta.code_name] =
          typeof raw === "object" && raw !== null
            ? raw
            : wrapStockFormValue(raw);
      } else {
        result[meta.code_name] = raw;
      }
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsMeta]);

  const generatedFields = useMemo(
    () =>
      fieldsMeta.map((meta) => {
        const field = buildFormFieldFromMeta(
          meta,
          initialValues[meta.code_name],
          "publish",
        );
        if (readStockConfig(meta.configuration_data)) {
          field.placeholder = t(
            "registrations.valueStock.searchPlaceholder",
            "Search available values",
          );
        }
        return field;
      }),
    [fieldsMeta, initialValues, t],
  );

  const renderEmptySubmit = useCallback(() => <></>, []);

  const formConfig = useMemo(
    () => ({
      name: formName,
      submit: renderEmptySubmit,
      fields: generatedFields,
    }),
    [formName, generatedFields, renderEmptySubmit],
  );

  // React Hook Form calls this only when the form passes validation. It collects
  // the form values into the wizard state and flags the submit as
  // successful so the wizard validator knows the form is valid.
  const collectedRef = useRef(false);
  const handleSubmit = useCallback(
    (values: Record<string, unknown>) => {
      collectedRef.current = true;
      const next = { ...values };
      for (const meta of fieldsMeta) {
        if (readStockConfig(meta.configuration_data)) {
          next[meta.code_name] = unwrapStockFormValue(next[meta.code_name]);
        }
      }
      updateRef.current({
        dynamicAttributes: {
          ...dynamicAttrsRef.current,
          ...next,
        },
      });
    },
    [fieldsMeta],
  );

  const formDeps = useMemo(
    () => [fieldsMeta.length, productUuid ?? ""],
    [fieldsMeta.length, productUuid],
  );
  const { Form, ref: formRef } = useForm(formConfig, handleSubmit, formDeps);

  useRegisterFormValidator(formName, formRef, collectedRef);

  return <Form className="flex flex-col gap-4" />;
}

export function AdditionalDetailsPanel({
  state,
  update,
  metadata,
  showDealer = false,
}: {
  state: WizardState;
  update: (payload: Partial<WizardState>) => void;
  metadata: RegistrationFormMetadata;
  showDealer?: boolean;
}) {
  const hostTemplateAttributes = metadata.host_template?.attributes;
  const hostAttrCodes = useMemo(() => {
    if (!hostTemplateAttributes) return new Set<string>();
    return new Set(hostTemplateAttributes.map((a) => a.code_name));
  }, [hostTemplateAttributes]);

  const hostMapsToColumns = useMemo(() => {
    if (!hostTemplateAttributes) return new Set<string>();
    return new Set(
      hostTemplateAttributes.map((a) => a.maps_to_column).filter(Boolean),
    );
  }, [hostTemplateAttributes]);

  const earlyAttrCodes = useMemo(() => {
    const codes = new Set(Object.keys(state.earlyAttributes ?? {}));
    for (const code of ((metadata as Record<string, unknown>)
      ?.early_registration_attributes as string[]) ?? []) {
      codes.add(code);
    }
    return codes;
  }, [state.earlyAttributes, metadata]);

  const dynamicFields = useMemo(() => {
    if (!metadata?.fields) return [];
    return metadata.fields.filter(
      (f) =>
        f.attribute_level === "HOLDING" &&
        !hostAttrCodes.has(f.code_name) &&
        !hostMapsToColumns.has(f.code_name) &&
        !earlyAttrCodes.has(f.code_name) &&
        // Attributes tagged with a `registration_group` render in their own
        // grouped panel (RegistrationFieldsPanel) via the wizard's extraPanels
        // slot, not in the generic Additional Details form. Config-driven and
        // tenant-agnostic (e.g. OC "Installed Components").
        !f.configuration_data?.registration_group,
    );
  }, [metadata.fields, hostAttrCodes, hostMapsToColumns, earlyAttrCodes]);

  useAutoSelectVehicleType(dynamicFields, state, update);

  if (dynamicFields.length === 0 && !showDealer) return null;

  return (
    <RegistrationPanel
      title="Additional Details"
      icon={<Settings2 size={14} className="text-[var(--ink-3)]" />}
    >
      <DetailFieldsForm
        formName="additional-details"
        fields={dynamicFields}
        state={state}
        update={update}
      />
      {showDealer && (
        <div className="mt-4">
          <InlineDealerField state={state} update={update} />
        </div>
      )}
    </RegistrationPanel>
  );
}

// ---------------------------------------------------------------------------
// Additional Detail Cards - fields split into two cards for 2×2 grid layout
// ---------------------------------------------------------------------------

// Code names that are already shown in the Vehicle/Host card lookup
const HIDDEN_DETAIL_CODES = new Set([
  "vehicle_registration_number",
  "registration_number",
  "reg_number",
]);

export function AdditionalDetailCards({
  state,
  update,
  metadata,
}: {
  state: WizardState;
  update: (payload: Partial<WizardState>) => void;
  metadata: RegistrationFormMetadata;
}) {
  const hostTemplateAttributes = metadata.host_template?.attributes;
  const hostAttrCodes = useMemo(() => {
    if (!hostTemplateAttributes) return new Set<string>();
    return new Set(hostTemplateAttributes.map((a) => a.code_name));
  }, [hostTemplateAttributes]);

  const hostMapsToColumns = useMemo(() => {
    if (!hostTemplateAttributes) return new Set<string>();
    return new Set(
      hostTemplateAttributes.map((a) => a.maps_to_column).filter(Boolean),
    );
  }, [hostTemplateAttributes]);

  const earlyAttrCodes2 = useMemo(() => {
    const codes = new Set(Object.keys(state.earlyAttributes ?? {}));
    for (const code of ((metadata as Record<string, unknown>)
      ?.early_registration_attributes as string[]) ?? []) {
      codes.add(code);
    }
    return codes;
  }, [state.earlyAttributes, metadata]);

  const dynamicFields = useMemo(() => {
    if (!metadata?.fields) return [];
    return metadata.fields.filter(
      (f) =>
        f.attribute_level === "HOLDING" &&
        !hostAttrCodes.has(f.code_name) &&
        !hostMapsToColumns.has(f.code_name) &&
        !HIDDEN_DETAIL_CODES.has(f.code_name) &&
        !earlyAttrCodes2.has(f.code_name) &&
        // See AdditionalDetailsPanel: `registration_group` attributes render
        // in their own grouped panel via extraPanels, not here.
        !f.configuration_data?.registration_group,
    );
  }, [metadata.fields, hostAttrCodes, hostMapsToColumns, earlyAttrCodes2]);

  useAutoSelectVehicleType(dynamicFields, state, update);

  const { leftFields, rightFields } = useMemo(() => {
    const mid = Math.ceil(dynamicFields.length / 2);
    return {
      leftFields: dynamicFields.slice(0, mid),
      rightFields: dynamicFields.slice(mid),
    };
  }, [dynamicFields]);

  if (dynamicFields.length === 0) return null;

  return (
    <>
      {/* Bottom-left card */}
      <RegistrationPanel
        title="Additional Details"
        icon={<Settings2 size={14} className="text-[var(--ink-3)]" />}
        className="panel-arrive"
      >
        <DetailFieldsForm
          formName="additional-detail-cards-left"
          fields={leftFields}
          state={state}
          update={update}
        />
      </RegistrationPanel>

      {/* Bottom-right card */}
      {rightFields.length > 0 && (
        <RegistrationPanel
          title="More Details"
          icon={<Settings2 size={14} className="text-[var(--ink-3)]" />}
          className="panel-arrive"
        >
          <DetailFieldsForm
            formName="additional-detail-cards-right"
            fields={rightFields}
            state={state}
            update={update}
          />
        </RegistrationPanel>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Dealer Selector
// ---------------------------------------------------------------------------

export function DealerSelector({
  state,
  update,
}: {
  state: WizardState;
  update: (payload: Partial<WizardState>) => void;
}) {
  const { orgUuid, isPartnerAccess, tenantSettings } = useOrg();
  const [search, setSearch] = useState("");
  // Suspended partners are dropped in SQL, before the page cap - filtering
  // them here let them eat the page and hide active dealers behind it.
  const { data: results, isFetching: searching } = usePartnershipSearch(
    orgUuid,
    search,
    { activeOnly: true },
  );

  if (isPartnerAccess) return null;

  const requirePartner =
    tenantSettings?.require_partner_on_registration ?? false;
  const partnerships = results ?? [];

  return (
    <div
      className={cn(
        "panel-arrive mt-4 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5 transition-[border-left] duration-150",
        state.partnershipUuid && "border-l-2 border-l-[var(--green)]",
      )}
    >
      <p className="mb-3 text-[10px] font-light uppercase tracking-[0.16em] text-[var(--ink-3)]">
        Dealer
        {requirePartner && (
          <span className="ml-0.5 inline-block mt-2 text-[17px] leading-[4px] text-[var(--status-error)]">
            *
          </span>
        )}
      </p>
      {state.partnershipUuid ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-[6px] border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]">
              <Building2 className="h-4 w-4" />
            </div>
            <span className="text-[13px] text-[var(--ink)]">
              {state.partnershipName}
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() =>
              update({ partnershipUuid: null, partnershipName: null })
            }
            className="h-6 w-6 cursor-pointer rounded-md text-[var(--ink-3)]"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ) : (
        <div>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search dealerships..."
            iconSize={12}
            className="h-9 text-[13px]"
          />
          {search.length >= 2 && partnerships.length > 0 && (
            <div className="mt-2 flex max-h-[180px] flex-col gap-0.5 overflow-y-auto border-t-[0.5px] border-t-[var(--rule)] pt-2">
              {partnerships.map((p) => (
                <Button
                  key={p.uuid}
                  variant="ghost"
                  onClick={() => {
                    update({
                      partnershipUuid: p.uuid,
                      partnershipName: p.downstream_organisation.name,
                    });
                    setSearch("");
                  }}
                  className="flex h-auto items-center justify-start gap-2.5 rounded-[4px] px-2 py-2 text-left font-light cursor-pointer"
                >
                  <Building2 className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                  <span className="text-[12px] text-[var(--ink)]">
                    {p.downstream_organisation.name}
                  </span>
                </Button>
              ))}
            </div>
          )}
          {search.length >= 2 && !searching && partnerships.length === 0 && (
            <p className="mt-2 text-center text-[11px] text-[var(--ink-3)]">
              No dealerships found
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline Dealer Field (for embedding inside Additional Details grid)
// ---------------------------------------------------------------------------

export function InlineDealerField({
  state,
  update,
  label = "Dealer",
  searchPlaceholder = "Search dealerships...",
  emptyMessage = "No dealerships found",
  error,
  secondaryAttributeCodes,
  badgeAttributeCode,
  searchOptions,
  onSelect,
  onClear,
  allowCreate = false,
  allowRecordCreate = false,
  createRoleUuid,
  createRoleName,
  onCreatedPartner,
  onSelectExistingPartner,
  createdName = null,
  onCreate,
  onClearCreated,
}: {
  state: WizardState;
  update: (payload: Partial<WizardState>) => void;
  label?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  error?: string;
  // Attribute code_names to render as a secondary disambiguator line
  // under each result, joined with " · " and skipping missing values.
  // Degrades cleanly on tenants that don't seed some / any of these.
  secondaryAttributeCodes?: string[];
  // Attribute code_name rendered as a right-aligned badge on each row
  // (typically the tier, so admins see access level at a glance).
  badgeAttributeCode?: string;
  // Passed straight through to usePartnershipSearch. Callers use this
  // to opt into sub-locations, narrow by role, etc.
  searchOptions?: PartnershipSearchOptions;
  // Extra side-effects when the user picks / clears a partnership.
  // Callers pass these to mirror the partnership name into adjacent
  // state (e.g. a required EAV attribute like ``installer_name``).
  onSelect?: (p: PartnershipOption) => void;
  onClear?: () => void;
  // When true, the user can capture a not-yet-onboarded contractor by typing
  // its name: an "Add '<name>'" row appears and calls ``onCreate(name)``. The
  // caller persists the free-text name itself (e.g. into an ``installer_name``
  // EAV attribute) with no partnership record - promoting it to a real
  // ``Partnership`` is a separate flow. Off by default (pure select).
  allowCreate?: boolean;
  // When true, offer "Can't find the <role>? Create New <role>", which opens
  // the inline capture form and creates a real partner record (WPS-163).
  // Supersedes ``allowCreate``'s free-text row, which only stores a name.
  allowRecordCreate?: boolean;
  createRoleUuid?: string | null;
  createRoleName?: string;
  onCreatedPartner?: (partnership: Partnership) => void;
  onSelectExistingPartner?: (candidate: DuplicatePartnerCandidate) => void;
  // The free-text contractor name the caller is currently holding (rendered as
  // the selected chip when no partnership is picked). Null = nothing captured.
  createdName?: string | null;
  onCreate?: (name: string) => void;
  onClearCreated?: () => void;
}) {
  const { t } = useTranslation();
  const { orgUuid } = useOrg();
  const scope = usePartnerPickerScope();
  const [search, setSearch] = useState("");
  // Whether the inline capture form is open.
  const [creating, setCreating] = useState(false);
  // The assigned partner being corrected in place, if any.
  const [editingPartner, setEditingPartner] = useState<Partnership | null>(
    null,
  );
  // A partner picks among its own locations, so the search has to reach
  // sub-locations even when the caller did not ask for them.
  // ``activeOnly`` narrows in SQL, ahead of the page cap. Filtering suspended
  // rows off the returned page instead let them consume it, so an active
  // dealer sorting past 25 for the term was unreachable.
  const effectiveSearchOptions = useMemo(
    () =>
      scope.ownLocationsOnly
        ? { ...searchOptions, includeLocations: true, activeOnly: true }
        : { ...searchOptions, activeOnly: true },
    [scope.ownLocationsOnly, searchOptions],
  );
  // Full profile of the assigned partner: tells us whether it was
  // hand-captured (so Edit applies) and prefills the edit form.
  const { data: selectedPartner } = usePartnershipDetail(
    state.partnershipUuid ?? null,
  );
  const { data: results, isFetching: searching } = usePartnershipSearch(
    orgUuid,
    search,
    effectiveSearchOptions,
  );

  if (!scope.visible) return null;

  const requirePartner = scope.required;
  const partnerships = results ?? [];
  // Already active-only from the server.
  const activePartnerships = partnerships;
  const trimmed = search.trim();
  // Offer "Add '<name>'" when the user has typed a usable name that no active
  // partnership already matches exactly, and either kind of creation is on:
  //   - allowRecordCreate: the row opens the capture modal (a real record).
  //   - allowCreate: the row stores the free-text name only (legacy path).
  // Never for a partner: onboarding a record into the tenant's network is an
  // internal act, and their own locations are the only rows they may pick.
  const canFreeTextCreate = allowCreate && !!onCreate;
  const canCreate =
    !scope.ownLocationsOnly &&
    (canFreeTextCreate || allowRecordCreate) &&
    trimmed.length >= 2 &&
    !activePartnerships.some(
      (p) =>
        p.downstream_organisation?.name?.trim().toLowerCase() ===
        trimmed.toLowerCase(),
    );
  // Correcting a partner record is an internal act; a partner only picks.
  const canEditAssignedPartner = allowRecordCreate && !scope.ownLocationsOnly;
  const hasSelection = !!state.partnershipUuid || !!createdName;

  return (
    <div>
      <Label className="mb-1.5 block text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {label}
        {requirePartner && (
          <span className="ml-0.5 inline-block mt-2 text-[17px] leading-[4px] text-[var(--status-error)]">
            *
          </span>
        )}
      </Label>
      {hasSelection ? (
        <>
          <div className="flex items-center justify-between rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2">
            <div className="flex items-center gap-2">
              {state.partnershipUuid ? (
                <Building2 className="h-3.5 w-3.5 text-[var(--green)]" />
              ) : (
                <UserPlus className="h-3.5 w-3.5 text-[var(--green)]" />
              )}
              <span className="text-[13px] text-[var(--ink)]">
                {state.partnershipUuid ? state.partnershipName : createdName}
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => {
                if (state.partnershipUuid) {
                  update({ partnershipUuid: null, partnershipName: null });
                  onClear?.();
                } else {
                  onClearCreated?.();
                }
              }}
              className="h-6 w-6 cursor-pointer rounded-md text-[var(--ink-3)]"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
          {/* Edit / Change directly from the assigned section, per the ticket.
            Edit shows only for a hand-captured partner: one the network
            supplied is owned there, and the API refuses a local edit the next
            sync would overwrite. */}
          {canEditAssignedPartner &&
            state.partnershipUuid &&
            !editingPartner && (
              <div className="mt-1.5 flex items-center gap-3">
                {selectedPartner?.origin === "MANUAL" && (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setEditingPartner(selectedPartner)}
                    className="h-auto px-0 text-[11px] text-[var(--green)]"
                  >
                    <Pencil className="mr-1 h-3 w-3" />
                    {t("registration.editPartner", "Edit {{role}}", {
                      role: createRoleName ?? label,
                    })}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => {
                    update({ partnershipUuid: null, partnershipName: null });
                    onClear?.();
                  }}
                  className="h-auto px-0 text-[11px] text-[var(--green)]"
                >
                  <RefreshCw className="mr-1 h-3 w-3" />
                  {t("registration.changePartner", "Change {{role}}", {
                    role: createRoleName ?? label,
                  })}
                </Button>
              </div>
            )}
          <PartnerCreateDialog
            open={!!editingPartner}
            editing={editingPartner}
            roleUuid={createRoleUuid}
            roleName={createRoleName ?? label}
            onCancel={() => setEditingPartner(null)}
            onCreated={(partnership) => {
              update({
                partnershipUuid: partnership.uuid,
                partnershipName: partnership.downstream_organisation.name,
              });
              onCreatedPartner?.(partnership);
              setEditingPartner(null);
            }}
            onSelectExisting={() => setEditingPartner(null)}
          />
        </>
      ) : (
        <div>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={searchPlaceholder}
            iconSize={12}
            className="h-9 text-[13px]"
          />
          {/* Say what a blank selection resolves to, so a partner browsing its
              locations knows leaving this alone is a valid choice. */}
          {scope.defaultsToName && (
            <p className="mt-1 text-[10px] text-[var(--ink-3)]">
              {t("registration.partnerDefaultsTo", "Defaults to {{name}}", {
                name: scope.defaultsToName,
              })}
            </p>
          )}
          {search.length >= 2 && activePartnerships.length > 0 && (
            <div className="mt-2 flex max-h-[180px] flex-col gap-0.5 overflow-y-auto border-t-[0.5px] border-t-[var(--rule)] pt-2">
              {activePartnerships
                .sort((a, b) => {
                  const aHq = (a.child_partnership_count ?? 0) > 0 ? 0 : 1;
                  const bHq = (b.child_partnership_count ?? 0) > 0 ? 0 : 1;
                  return aHq - bHq;
                })
                .map((p) => (
                  <Button
                    key={p.uuid}
                    variant="ghost"
                    onClick={() => {
                      update({
                        partnershipUuid: p.uuid,
                        partnershipName: p.downstream_organisation.name,
                      });
                      onSelect?.(p);
                      setSearch("");
                    }}
                    className="flex h-auto w-full items-start justify-start gap-2.5 px-2 py-2 rounded-[4px] text-left font-light cursor-pointer"
                  >
                    <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-[12px] text-[var(--ink)]">
                          {p.downstream_organisation.name}
                        </span>
                        {(p.child_partnership_count ?? 0) > 0 && (
                          <span className="shrink-0 rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] font-light uppercase tracking-[0.1em] text-[var(--ink-3)]">
                            Headquarters
                          </span>
                        )}
                      </div>
                      {(() => {
                        const parts = (secondaryAttributeCodes ?? [])
                          .map((code) => p.attribute_summary?.[code])
                          .filter(
                            (v): v is string =>
                              typeof v === "string" && v.trim().length > 0,
                          );
                        if (parts.length === 0) return null;
                        return (
                          <span className="truncate text-[10px] text-[var(--ink-3)]">
                            {parts.join(" · ")}
                          </span>
                        );
                      })()}
                    </div>
                    {badgeAttributeCode &&
                      p.attribute_summary?.[badgeAttributeCode] && (
                        <span className="ml-auto shrink-0 self-start rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] font-light uppercase tracking-[0.1em] text-[var(--ink-3)]">
                          {String(p.attribute_summary[badgeAttributeCode])}
                        </span>
                      )}
                  </Button>
                ))}
            </div>
          )}
          {canCreate && (
            <Button
              variant="ghost"
              onClick={() => {
                // Record-create tenants open the capture modal (prefilled with
                // the typed name, so ``trimmed`` must survive - don't clear the
                // search here). Free-text tenants just store the name.
                if (allowRecordCreate) {
                  setCreating(true);
                } else {
                  onCreate?.(trimmed);
                  setSearch("");
                }
              }}
              className="mt-2 flex h-auto w-full items-center justify-start gap-2.5 rounded-[4px] px-2 py-2 text-left font-light cursor-pointer"
            >
              <Plus className="h-3.5 w-3.5 shrink-0 text-[var(--green)]" />
              <span className="truncate text-[12px] text-[var(--ink)]">
                {t("common.addWithName", 'Add "{{name}}"', { name: trimmed })}
              </span>
            </Button>
          )}
          {allowRecordCreate && (
            <PartnerCreateDialog
              open={creating}
              roleUuid={createRoleUuid}
              roleName={createRoleName ?? label}
              initialCompanyName={trimmed}
              onCancel={() => setCreating(false)}
              onCreated={(partnership) => {
                update({
                  partnershipUuid: partnership.uuid,
                  partnershipName: partnership.downstream_organisation.name,
                });
                onCreatedPartner?.(partnership);
                setCreating(false);
                setSearch("");
              }}
              onSelectExisting={(candidate) => {
                update({
                  partnershipUuid: candidate.partnership_uuid,
                  partnershipName: candidate.name,
                });
                onSelectExistingPartner?.(candidate);
                setCreating(false);
                setSearch("");
              }}
            />
          )}
          {/* Only once the fetch has settled - mid-flight there are no rows
              yet, and "none found" is not true of a search still running. */}
          {search.length >= 2 &&
            !searching &&
            activePartnerships.length === 0 &&
            !canCreate && (
              <p className="mt-2 text-center text-[11px] text-[var(--ink-3)]">
                {emptyMessage}
              </p>
            )}
        </div>
      )}
      {error && (
        <p className="mt-1 text-xs text-[var(--status-error)]">{error}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

export function ProductRow({
  product,
  selected,
  onSelect,
  isLast,
}: {
  product: Product;
  selected: boolean;
  hasSelection?: boolean;
  onSelect: () => void;
  isLast: boolean;
}) {
  return (
    <button
      onClick={onSelect}
      data-selected={selected || undefined}
      className={cn(
        "flex min-h-[44px] w-full cursor-pointer items-center gap-3 border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3 text-left transition-all duration-150 data-[selected]:bg-[var(--green-bg)] hover:not-data-[selected]:bg-[var(--bg)]",
        isLast ? "border-b-0" : "",
      )}
    >
      {/* Radio circle */}
      <span
        className={cn(
          "flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-full border-[0.5px] transition-all duration-150",
          selected
            ? "border-[var(--green)] bg-[var(--green)]"
            : "border-[var(--rule-strong)] bg-white",
        )}
      >
        {selected && <span className="h-[5px] w-[5px] rounded-full bg-white" />}
      </span>
      <span
        className={cn(
          "flex-1 text-[13px] transition-colors duration-150",
          selected ? "font-light text-[var(--green)]" : "text-[var(--ink)]",
        )}
      >
        {product.cached_short_name}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// FlatProductRow - flat-mode row used by ProductPanel.
//
// Differs from `ProductRow` in two ways:
//  1. Parent products (VARIANT_BASE / variant_count > 0) render with a
//     chevron-right affordance + variant count instead of a radio, since
//     clicking them drills into a sub-list rather than completing the
//     selection.
//  2. Standalone products keep the existing radio + green-on-select
//     visual treatment from `ProductRow`.
// ---------------------------------------------------------------------------

function FlatProductRow({
  product,
  variantAxisLabel,
  selected,
  onSelect,
  isLast,
}: {
  product: Product;
  variantAxisLabel?: string;
  selected: boolean;
  onSelect: () => void;
  isLast: boolean;
}) {
  const isParent =
    product.product_type === "VARIANT_BASE" || (product.variant_count ?? 0) > 0;
  const variantCount = product.variant_count ?? 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      data-selected={selected || undefined}
      className={cn(
        "group flex min-h-[48px] w-full cursor-pointer items-center gap-3 border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3 text-left transition-all duration-150 data-[selected]:bg-[var(--green-bg)] hover:not-data-[selected]:bg-[var(--bg)]",
        isLast ? "border-b-0" : "",
      )}
    >
      {isParent ? (
        <span className="flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-[3px] border-[0.5px] border-[var(--rule-strong)] bg-white text-[var(--ink-3)] transition-colors duration-150 group-hover:border-[var(--ink-3)] group-hover:text-[var(--ink)]">
          <Package size={9} />
        </span>
      ) : (
        <span
          className={cn(
            "flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-full border-[0.5px] transition-all duration-150",
            selected
              ? "border-[var(--green)] bg-[var(--green)]"
              : "border-[var(--rule-strong)] bg-white",
          )}
        >
          {selected && (
            <span className="h-[5px] w-[5px] rounded-full bg-white" />
          )}
        </span>
      )}

      <span
        className={cn(
          "flex-1 truncate text-[13px] transition-colors duration-150",
          selected ? "font-light text-[var(--green)]" : "text-[var(--ink)]",
        )}
      >
        {product.cached_short_name}
      </span>

      {isParent ? (
        <span className="flex shrink-0 items-center gap-1.5">
          <span className="rounded-full border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-2 py-0.5 text-[10px] font-light tracking-[0.04em] text-[var(--ink-3)]">
            {formatVariantCountLabel(variantCount, variantAxisLabel)}
          </span>
          <ChevronRight
            size={14}
            className="text-[var(--ink-4)] transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-[var(--ink-3)]"
          />
        </span>
      ) : null}
    </button>
  );
}

// ---------------------------------------------------------------------------
// CategoryCard - large card with icon, label, and product count
// ---------------------------------------------------------------------------

function CategoryCard({
  label,
  productCount,
  icon,
  active,
  onClick,
}: {
  label: string;
  productCount: number;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex cursor-pointer flex-col items-start rounded-[8px] border-[0.5px] p-4 text-left transition-all duration-150",
        active
          ? "border-[var(--green-border)] bg-[var(--green-bg)]"
          : "border-[var(--rule)] bg-[var(--bg-light)] hover:border-[var(--rule-strong)]",
      )}
    >
      <div
        className={cn(
          "mb-2.5 transition-opacity duration-150",
          active ? "opacity-100" : "opacity-40",
        )}
      >
        {icon}
      </div>
      <p
        className={cn(
          "text-[13px] transition-colors duration-150",
          active ? "font-light text-[var(--green)]" : "text-[var(--ink)]",
        )}
      >
        {label}
      </p>
      <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
        {productCount} {productCount === 1 ? "product" : "products"}
      </p>
    </button>
  );
}

// CategoryIcon is now imported from @/admin/components/category-icons

export function Field({
  label,
  value,
  placeholder,
  onChange,
  required,
  readOnly,
  error,
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (v: string) => void;
  required?: boolean;
  readOnly?: boolean;
  error?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="gap-0 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {label}
        {required && (
          <span className="ml-0.5 inline-block mt-2 text-[17px] leading-[4px] text-[var(--status-error)]">
            *
          </span>
        )}
      </Label>
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        autoComplete="off"
        data-lpignore="true"
        data-form-type="other"
        className={cn(
          "h-9 w-full rounded-[4px] border-[0.5px] px-3 text-[13px] text-[var(--ink)]",
          readOnly
            ? "bg-[var(--bg)] cursor-default"
            : "bg-[var(--bg)] focus-visible:border-[var(--green)]",
          error ? "border-destructive" : "border-[var(--rule)]",
        )}
      />
      {error ? (
        <p className="text-xs text-[var(--status-error)]">{error}</p>
      ) : null}
    </div>
  );
}

export function StyledSelect({
  label,
  value,
  options,
  onChange,
  required,
  hint,
  error,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  required?: boolean;
  hint?: string;
  error?: string;
}) {
  const [open, setOpen] = useState(false);
  const selectedLabel = options.find((o) => o.value === value)?.label;

  return (
    <div className="flex flex-col gap-1.5 relative">
      <Label className="gap-0 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {label}
        {required && (
          <span className="ml-0.5 inline-block mt-2 text-[17px] leading-[4px] text-[var(--status-error)]">
            *
          </span>
        )}
      </Label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "flex h-9 w-full cursor-pointer items-center justify-between rounded-[4px] border-[0.5px] bg-[var(--bg)] px-3 text-left text-[13px] transition-colors duration-150",
          open
            ? "border-[var(--green)]"
            : error
              ? "border-destructive"
              : "border-[var(--rule)]",
          value ? "text-[var(--ink)]" : "text-[var(--ink-3)]",
        )}
      >
        <span className="truncate">
          {selectedLabel || hint || `Select ${label.toLowerCase()}...`}
        </span>
        <ChevronDown
          size={13}
          className={cn(
            "ml-2 shrink-0 text-[var(--ink-3)] transition-transform duration-150",
            open && "rotate-180",
          )}
        />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-[200px] overflow-y-auto overflow-hidden rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] shadow-sm">
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`flex min-h-[34px] w-full cursor-pointer items-center justify-between border-b-[0.5px] border-[var(--rule)] px-3 text-left text-[13px] transition-colors duration-150 ${
                  opt.value === value
                    ? "text-[var(--green)] bg-[var(--green-bg)]"
                    : "text-[var(--ink)] hover:bg-[var(--bg-subtle)]"
                }`}
              >
                <span>{opt.label}</span>
                {opt.value === value && (
                  <Check size={12} className="text-[var(--green)]" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
      {error ? (
        <p className="text-xs text-[var(--status-error)]">{error}</p>
      ) : null}
    </div>
  );
}

export function ToggleField({
  label,
  checked,
  onChange,
  required,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  required?: boolean;
  hint?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="gap-0 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {label}
        {required && (
          <span className="ml-0.5 inline-block mt-2 text-[17px] leading-[4px] text-[var(--status-error)]">
            *
          </span>
        )}
      </Label>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className="flex h-9 w-full cursor-pointer items-center gap-3 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3"
      >
        <span
          className={cn(
            "relative inline-block h-[18px] w-8 shrink-0 rounded-full transition-colors duration-150",
            checked ? "bg-[var(--green)]" : "bg-[var(--rule-strong)]",
          )}
        >
          <span
            className={cn(
              "absolute top-[2px] h-[14px] w-[14px] rounded-full bg-[var(--bg)] transition-[left] duration-150",
              checked ? "left-4" : "left-[2px]",
            )}
          />
        </span>
        <span className="text-[13px] text-[var(--ink)]">
          {hint || (checked ? "Yes" : "No")}
        </span>
      </button>
    </div>
  );
}
